import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Grid,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import ThisOrThatCompletion from "./ThisOrThatCompletion";
import { useSpring, animated } from "@react-spring/web";

// Sample pairs - these would be configurable based on the brand
const DEFAULT_PAIRS = [
  { left: "🧋 Iced Latte", right: "☕️ Hot Coffee" },
  { left: "🍕 Pizza", right: "🍔 Burger" },
  { left: "🏖️ Beach", right: "🏔️ Mountains" },
  { left: "🐶 Dogs", right: "🐱 Cats" },
  { left: "🌅 Sunrise", right: "🌄 Sunset" },
  { left: "📱 Android", right: "🍎 iPhone" },
  { left: "🎮 Console", right: "💻 PC" },
  { left: "🍿 Movies", right: "📺 TV Shows" },
  { left: "🎵 Music", right: "🎧 Podcasts" },
  { left: "📚 Books", right: "🎮 Games" },
];

// Mock results - in a real app, these would come from an API
const MOCK_RESULTS = {
  "🧋 Iced Latte": 72,
  "☕️ Hot Coffee": 28,
  "🍕 Pizza": 65,
  "🍔 Burger": 35,
  "🏖️ Beach": 58,
  "🏔️ Mountains": 42,
  "🐶 Dogs": 70,
  "🐱 Cats": 30,
  "🌅 Sunrise": 45,
  "🌄 Sunset": 55,
  "📱 Android": 40,
  "🍎 iPhone": 60,
  "🎮 Console": 55,
  "💻 PC": 45,
  "🍿 Movies": 48,
  "📺 TV Shows": 52,
  "🎵 Music": 62,
  "🎧 Podcasts": 38,
  "📚 Books": 35,
  "🎮 Games": 65,
  // HERO_PAIRS mock data:
  "🎮 Console Gaming": 60,
  "💻 PC Gaming": 40,
  "🍿 Movie Night": 68,
  "🎮 Game Night": 32,
  "📱 Smartphone": 55,
  "💻 Laptop": 45,
  "🎵 Spotify": 73,
  "🎧 Apple Music": 27,
  "📺 Netflix": 64,
  "🎮 Twitch": 36,
  "📚 Physical Books": 41,
  "📱 E-books": 59,
  "🎮 Single Player": 48,
  "👥 Multiplayer": 52,
  "🎮 Retro Games": 33,
  "🎮 Modern Games": 67,
  "🎮 Casual Games": 44,
  "🎮 Hardcore Games": 56,
  "🎮 Mobile Games": 38,
  "🎮 Console Games": 62,
};

const ThisOrThat = ({
  pairs = DEFAULT_PAIRS,
  onComplete,
  brandColors = {
    primary: "#FF61D8",
    secondary: "#6366F1",
    background: "#F8F0FF",
    text: "#444",
    success: "rgba(40, 180, 99, 0.32)",
    neutral: "rgba(60, 80, 180, 0.28)",
    progressBg: "#f3f3fa",
    white: "#fff",
    black: "#000",
  },
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentRound, setCurrentRound] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [shuffledPairs, setShuffledPairs] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [majorityPicks, setMajorityPicks] = useState(0);
  const [mockResults, setMockResults] = useState({});
  const containerRef = useRef(null);
  const confettiCanvasRef = useRef(null);

  // Initialize random results for each pair when component mounts
  useEffect(() => {
    const results = {};
    pairs.forEach((pair) => {
      // Generate random percentage between 30 and 70
      const leftPercentage = Math.floor(Math.random() * 41) + 30;
      results[pair.left] = leftPercentage;
      results[pair.right] = 100 - leftPercentage;
    });
    setMockResults(results);
  }, [pairs]);

  // Shuffle pairs on component mount
  useEffect(() => {
    setShuffledPairs([...pairs].sort(() => Math.random() - 0.5));
  }, [pairs]);

  // Confetti instance for container
  const fireConfetti = () => {
    if (!confettiCanvasRef.current) return;
    confetti.create(confettiCanvasRef.current, {
      resize: true,
      useWorker: true,
    })({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });
  };

  const handleChoice = (choice) => {
    if (selected || isTransitioning) return; // Prevent multiple selections or during transition

    setSelected(choice);
    setShowResults(true);

    // Trigger haptic feedback if available
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }

    // Check if user picked the majority choice
    const result = mockResults[choice];
    if (result > 50) {
      setMajorityPicks((prev) => prev + 1);
      fireConfetti();
    }

    // Move to next round after delay
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        if (currentRound < pairs.length - 1) {
          setCurrentRound((prev) => prev + 1);
          setSelected(null);
          setShowResults(false);
        } else {
          setGameComplete(true);
          onComplete?.();
        }
        setIsTransitioning(false);
      }, 500); // Transition duration
    }, 1500); // Show results duration
  };

  const handlePlayAgain = () => {
    setCurrentRound(0);
    setSelected(null);
    setShowResults(false);
    setGameComplete(false);
    setMajorityPicks(0);
    setShuffledPairs([...pairs].sort(() => Math.random() - 0.5));
  };

  const currentPair = shuffledPairs[currentRound];

  // Progress bar animation
  const progress = ((currentRound + 1) / pairs.length) * 100;
  const progressSpring = useSpring({
    to: { value: progress },
    config: { tension: 170, friction: 26, duration: 400 },
  });

  // Helper to get mock result with random percentage
  const getMockResult = (choice) => {
    return mockResults[choice] || 50;
  };

  // Helper to get overlay color and emoji based on majority
  const getResultOverlay = (percent) => {
    if (percent >= 50) {
      return {
        background: brandColors.success,
        emoji: "🎉",
      };
    } else {
      return {
        background: brandColors.neutral,
        emoji: "😬",
      };
    }
  };

  if (!currentPair) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: brandColors.background,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Title and subtitle header */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          background: "black",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          mb: 1,
          fontSize: { xs: "1.2rem", md: "1.5rem" },
          letterSpacing: 0.5,
          textAlign: "center",
        }}
      >
        This or That
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: brandColors.text,
          opacity: 0.92,
          fontWeight: 400,
          fontSize: { xs: "0.98rem", md: "1.08rem" },
          mb: 2,
          textAlign: "center",
        }}
      >
        Find out what your peers think!
      </Typography>
      {/* Confetti canvas absolutely positioned over the game container */}
      <canvas
        ref={confettiCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 10,
        }}
        width={containerRef.current?.offsetWidth || 1}
        height={containerRef.current?.offsetHeight || 1}
      />
      <AnimatePresence mode="wait">
        {gameComplete ? (
          <ThisOrThatCompletion
            onPlayAgain={handlePlayAgain}
            totalRounds={pairs.length}
            majorityPicks={majorityPicks}
            brandColors={brandColors}
          />
        ) : (
          <motion.div
            key="game"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ width: "100%" }}
          >
            {/* Progress indicator */}
            <Box sx={{ width: "100%", maxWidth: 600, mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    ml: 1,
                    letterSpacing: 0.1,
                    fontSize: "0.85rem",
                  }}
                >
                  {currentRound + 1} of {pairs.length}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    mr: 1,
                    letterSpacing: 0.1,
                    fontSize: "0.85rem",
                  }}
                >
                  {Math.round(((currentRound + 1) / pairs.length) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: brandColors.progressBg,
                  "& .MuiLinearProgress-bar1Determinate": {
                    background: `linear-gradient(90deg, ${brandColors.primary}, ${brandColors.secondary})`,
                    borderRadius: 6,
                    boxShadow: `0 2px 8px ${brandColors.secondary}10`,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: 600,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 2,
                position: "relative",
                mb: 4,
              }}
            >
              {/* Left choice */}
              <motion.div
                style={{ flex: 1 }}
                animate={{
                  scale:
                    showResults && selected === currentPair.left ? 1.05 : 1,
                  opacity: showResults
                    ? selected === currentPair.left
                      ? 1
                      : 0.5
                    : 1,
                }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  fullWidth
                  onClick={() => handleChoice(currentPair.left)}
                  disabled={selected || isTransitioning}
                  sx={{
                    height: isMobile ? 90 : 140,
                    borderRadius: "20px 20px 20px 5px",
                    background: brandColors.primary,
                    color: brandColors.text || "#222222",
                    fontSize: { xs: "1.18rem", md: "1.28rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    position: "relative",
                    overflow: "hidden",
                    border: `2px solid ${brandColors.primary}`,
                    boxShadow:
                      showResults && selected === currentPair.left
                        ? "0 6px 0px rgba(0,0,0,0.25)"
                        : "0 4px 0px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.05)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "none",
                      opacity: 1,
                    },
                    "&:hover": {
                      background: brandColors.primary,
                      opacity: 0.96,
                      boxShadow: "0 6px 0px rgba(0,0,0,0.25)",
                      transform: isMobile ? "none" : "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1.5em",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                        mb: 0.5,
                      }}
                    >
                      {currentPair.left.split(" ")[0]}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "0.9em",
                        fontStyle: "italic",
                        opacity: 0.9,
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      {currentPair.left.split(" ").slice(1).join(" ")}
                    </Box>
                  </Box>
                  {showResults &&
                    selected === currentPair.left &&
                    (() => {
                      const percent = getMockResult(currentPair.left);
                      const { background, emoji } = getResultOverlay(percent);
                      return (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            background,
                            borderRadius: "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            pointerEvents: "none",
                            transition:
                              "background 0.3s cubic-bezier(.4,0,.2,1)",
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              color: brandColors.white,
                              fontWeight: 900,
                              fontSize: { xs: "1.45rem", md: "1.7rem" },
                              letterSpacing: 0.5,
                              textShadow: `0 2px 8px ${brandColors.black}45, 0 1px 0 ${brandColors.white}, 0 0.5px 0 ${brandColors.white}`,
                              textAlign: "center",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {percent >= 50
                              ? `${percent}% ${emoji} agree!`
                              : `Only ${percent}% agree with you 😬`}
                          </Typography>
                        </Box>
                      );
                    })()}
                </Button>
              </motion.div>

              {/* Right choice */}
              <motion.div
                style={{ flex: 1 }}
                animate={{
                  scale:
                    showResults && selected === currentPair.right ? 1.05 : 1,
                  opacity: showResults
                    ? selected === currentPair.right
                      ? 1
                      : 0.5
                    : 1,
                }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  fullWidth
                  onClick={() => handleChoice(currentPair.right)}
                  disabled={selected || isTransitioning}
                  sx={{
                    height: isMobile ? 90 : 140,
                    borderRadius: "20px 20px 5px 20px",
                    background: brandColors.secondary,
                    color: brandColors.text || "#222222",
                    fontSize: { xs: "1.18rem", md: "1.28rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    position: "relative",
                    overflow: "hidden",
                    border: `2px solid ${brandColors.secondary}`,
                    boxShadow:
                      showResults && selected === currentPair.right
                        ? "0 6px 0px rgba(0,0,0,0.25)"
                        : "0 4px 0px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.05)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "none",
                      opacity: 1,
                    },
                    "&:hover": {
                      background: brandColors.secondary,
                      opacity: 0.96,
                      boxShadow: "0 6px 0px rgba(0,0,0,0.25)",
                      transform: isMobile ? "none" : "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1.5em",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                        mb: 0.5,
                      }}
                    >
                      {currentPair.right.split(" ")[0]}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "0.9em",
                        fontStyle: "italic",
                        opacity: 0.9,
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      {currentPair.right.split(" ").slice(1).join(" ")}
                    </Box>
                  </Box>
                  {showResults &&
                    selected === currentPair.right &&
                    (() => {
                      const percent = getMockResult(currentPair.right);
                      const { background, emoji } = getResultOverlay(percent);
                      return (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            background,
                            borderRadius: "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            pointerEvents: "none",
                            transition:
                              "background 0.3s cubic-bezier(.4,0,.2,1)",
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              color: brandColors.white,
                              fontWeight: 900,
                              fontSize: { xs: "1.45rem", md: "1.7rem" },
                              letterSpacing: 0.5,
                              textShadow: `0 2px 8px ${brandColors.black}45, 0 1px 0 ${brandColors.white}, 0 0.5px 0 ${brandColors.white}`,
                              textAlign: "center",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {percent >= 50
                              ? `${percent}% ${emoji} agree!`
                              : `Only ${percent}% agree with you 😬`}
                          </Typography>
                        </Box>
                      );
                    })()}
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default ThisOrThat;
